// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * Bootstraps zipsafe: wires the UI, the event drawer, the glossary, the modals,
 * and the worker-backed zip/unzip flows. Owns the domain state; delegates all
 * DOM work to ui.ts and all heavy lifting to the worker via zipclient.ts.
 */

import './styles/main.css';
import { createUI, type UI } from './ui';
import { emit, mountEventDrawer } from './eventlog';
import { initGlossary } from './glossary';
import { createZip, extractZip, listZip, resetWorker } from './zipclient';
import {
  downloadBlob,
  saveArchive,
  saveToDirectory,
  shareArchive,
} from './fsaccess';
import { defaultArchiveName, formatBytes, formatThroughput } from './format';
import type { ExtractedFile, InputFile, Mode, ZipEntry } from './types';

const PREF_KEY = 'zipsafe:mode';

interface State {
  mode: Mode;
  sourceFile: File | null;
  entries: ZipEntry[];
  extracted: ExtractedFile[];
  queue: File[];
  built: { data: Uint8Array; name: string; originalSize: number } | null;
}

const state: State = {
  mode: 'extract',
  sourceFile: null,
  entries: [],
  extracted: [],
  queue: [],
  built: null,
};

let ui: UI;
let opStart = 0;

function canShareFiles(): boolean {
  const nav = navigator as Navigator & { canShare?: unknown; share?: unknown };
  return typeof nav.share === 'function' && typeof nav.canShare === 'function';
}

function trackProgress(done: number, total: number, label: string): void {
  ui.setProgress(done, total, label);
  const elapsed = Date.now() - opStart;
  if (elapsed > 200) ui.setThroughput(formatThroughput(done, elapsed));
}

function fail(message: string): void {
  emit('system', 'err', message);
  ui.showError(message);
  ui.hideProgress();
}

/* ───────── extract flow ───────── */

async function ingestZip(file: File): Promise<void> {
  state.sourceFile = file;
  ui.setStatus('reading', 'busy');
  emit('zip', 'info', `opening ${file.name} (${formatBytes(file.size)})`);
  try {
    const buffer = await file.arrayBuffer();
    const entries = await listZip(buffer);
    state.entries = entries;
    const fileCount = entries.filter((e) => !e.isDir).length;
    emit('zip', 'ok', `read ${fileCount} entr${fileCount === 1 ? 'y' : 'ies'} from central directory`);
    ui.showTree(entries, file.name);
    ui.setStatus('ready', 'idle');
  } catch (err) {
    resetWorker();
    fail(friendly(err, 'Could not read that ZIP — it may be corrupt or not a real archive.'));
    ui.setStatus('error', 'err');
  }
}

async function doExtract(paths: string[] | null): Promise<void> {
  if (!state.sourceFile) return;
  ui.setStatus('extracting', 'busy');
  opStart = Date.now();
  const count = paths ? paths.length : state.entries.filter((e) => !e.isDir).length;
  emit('zip', 'info', `extracting ${count} file${count === 1 ? '' : 's'}`);
  try {
    const buffer = await state.sourceFile.arrayBuffer();
    const files = await extractZip(buffer, paths ?? undefined, (d, t) =>
      trackProgress(d, t, 'Decompressing…'),
    );
    state.extracted = files;
    ui.hideProgress();
    ui.showExtracted(files);
    ui.setStatus('done', 'ok');
    emit('zip', 'ok', `extracted ${files.length} file${files.length === 1 ? '' : 's'} into memory`);
  } catch (err) {
    resetWorker();
    fail(friendly(err, 'Extraction failed.'));
    ui.setStatus('error', 'err');
  }
}

async function saveExtractedToFolder(): Promise<void> {
  if (!state.extracted.length) return;
  try {
    emit('fs', 'info', 'choosing destination folder…');
    const written = await saveToDirectory(state.extracted);
    emit('fs', 'ok', `wrote ${written} file${written === 1 ? '' : 's'} to your folder`);
    ui.setStatus('saved', 'ok');
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      emit('fs', 'warn', 'folder save cancelled');
      return;
    }
    fail(friendly(err, 'Could not save to that folder.'));
  }
}

function downloadOne(path: string): void {
  const file = state.extracted.find((f) => f.path === path);
  if (!file) return;
  const name = path.split('/').pop() || 'file';
  downloadBlob(file.data, name);
  emit('fs', 'ok', `downloaded ${name}`);
}

/* ───────── create flow ───────── */

function addToQueue(files: File[]): void {
  state.queue.push(...files);
  const queueAsInput: InputFile[] = state.queue.map((f) => ({
    name: f.name,
    size: f.size,
    data: new Uint8Array(0), // placeholder; real bytes read at create time
  }));
  emit('ui', 'info', `${files.length} file${files.length === 1 ? '' : 's'} added to queue`);
  ui.showCreateQueue(queueAsInput);
}

async function doCreate(level: number): Promise<void> {
  if (!state.queue.length) return;
  ui.setStatus('zipping', 'busy');
  opStart = Date.now();
  emit('zip', 'info', `compressing ${state.queue.length} file${state.queue.length === 1 ? '' : 's'} at level ${level}`);
  try {
    const inputs: InputFile[] = [];
    for (const f of state.queue) {
      inputs.push({ name: f.name, size: f.size, data: new Uint8Array(await f.arrayBuffer()) });
    }
    const name = defaultArchiveName(state.queue);
    const data = await createZip(inputs, level, (d, t) => trackProgress(d, t, 'Compressing…'));
    const originalSize = state.queue.reduce((n, f) => n + f.size, 0);
    state.built = { data, name, originalSize };
    ui.hideProgress();
    ui.showCreated(data.length, originalSize, name, canShareFiles());
    ui.setStatus('done', 'ok');
    emit('zip', 'ok', `built ${name} (${formatBytes(data.length)})`);
  } catch (err) {
    resetWorker();
    fail(friendly(err, 'Could not build the archive.'));
    ui.setStatus('error', 'err');
  }
}

async function saveBuilt(): Promise<void> {
  if (!state.built) return;
  try {
    const ok = await saveArchive(state.built.data, state.built.name);
    if (ok) {
      emit('fs', 'ok', `saved ${state.built.name}`);
      ui.setStatus('saved', 'ok');
    } else {
      emit('fs', 'warn', 'save cancelled');
    }
  } catch (err) {
    fail(friendly(err, 'Could not save the archive.'));
  }
}

async function shareBuilt(): Promise<void> {
  if (!state.built) return;
  try {
    const shared = await shareArchive(state.built.data, state.built.name);
    if (shared) emit('fs', 'ok', `shared ${state.built.name}`);
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') return;
    fail(friendly(err, 'Sharing failed.'));
  }
}

/* ───────── sample ───────── */

async function loadSample(): Promise<void> {
  try {
    emit('ui', 'info', 'loading sample archive');
    const res = await fetch('samples/demo.zip');
    if (!res.ok) throw new Error(`sample fetch ${res.status}`);
    const blob = await res.blob();
    const file = new File([blob], 'demo.zip', { type: 'application/zip' });
    // The sample demonstrates opening a ZIP — make sure we're in extract mode,
    // then route it through the exact same path a dropped/chosen file takes.
    if (state.mode !== 'extract') setMode('extract');
    onFiles([file]);
  } catch (err) {
    fail(friendly(err, 'Could not load the sample archive.'));
  }
}

/* ───────── shared ───────── */

function onFiles(files: File[]): void {
  if (!files.length) return;
  if (state.mode === 'extract') {
    const zip = files.find((f) => /\.zip$/i.test(f.name) || f.type === 'application/zip');
    if (!zip) {
      fail('That doesn’t look like a .zip file. Switch to “Create a ZIP” to bundle files instead.');
      return;
    }
    void ingestZip(zip);
  } else {
    addToQueue(files);
  }
}

function setMode(mode: Mode): void {
  state.mode = mode;
  state.sourceFile = null;
  state.entries = [];
  state.extracted = [];
  state.queue = [];
  state.built = null;
  ui.setMode(mode);
  ui.setStatus('ready', 'idle');
  ui.setThroughput('—');
  try {
    localStorage.setItem(PREF_KEY, mode);
  } catch {
    /* private mode — ignore */
  }
  emit('ui', 'info', `mode: ${mode}`);
}

function reset(): void {
  setMode(state.mode);
}

function friendly(err: unknown, fallback: string): string {
  const msg = err instanceof Error ? err.message : String(err);
  return msg && msg !== 'Unexpected worker response' ? `${fallback} (${msg})` : fallback;
}

/* ───────── modals ───────── */

function mountModals(): void {
  let overlay: HTMLElement | null = null;

  const close = () => {
    if (overlay) {
      overlay.remove();
      overlay = null;
    }
  };

  const open = (templateId: string) => {
    const tmpl = document.getElementById(templateId) as HTMLTemplateElement | null;
    if (!tmpl) return;
    close();
    overlay = document.createElement('div');
    overlay.className = 'modal-overlay';
    const panel = document.createElement('div');
    panel.className = 'modal-panel';
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    const closeBtn = document.createElement('button');
    closeBtn.className = 'modal-close';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.innerHTML = '✕';
    closeBtn.addEventListener('click', close);
    panel.appendChild(closeBtn);
    panel.appendChild(tmpl.content.cloneNode(true));
    overlay.appendChild(panel);
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) close();
    });
    document.body.appendChild(overlay);
  };

  document.querySelectorAll<HTMLElement>('[data-modal]').forEach((btn) => {
    btn.addEventListener('click', () => open(btn.dataset.modal!));
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') close();
  });
}

/* ───────── boot ───────── */

function boot(): void {
  const app = document.getElementById('app');
  const drawer = document.getElementById('event-drawer');
  if (!app || !drawer) return;

  ui = createUI(app, {
    onFiles,
    onSample: () => void loadSample(),
    onModeChange: setMode,
    onExtract: (paths) => void doExtract(paths),
    onSaveExtractedToFolder: () => void saveExtractedToFolder(),
    onDownloadExtracted: downloadOne,
    onCreate: (level) => void doCreate(level),
    onSaveArchive: () => void saveBuilt(),
    onShareArchive: () => void shareBuilt(),
    onReset: reset,
  });

  mountEventDrawer(drawer);
  initGlossary();
  mountModals();

  let initial: Mode = 'extract';
  try {
    const saved = localStorage.getItem(PREF_KEY);
    if (saved === 'create' || saved === 'extract') initial = saved;
  } catch {
    /* ignore */
  }
  setMode(initial);
  emit('system', 'ok', 'zipsafe ready — everything runs in this tab, nothing is uploaded');
}

boot();
